import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabaseClient";

const DonationModal = ({ artwork, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [donationDetails, setDonationDetails] = useState({
    valuationAmount: artwork?.value || 0,
    recipient: "",
    date: new Date().toISOString().split('T')[0],
    notes: ""
  });
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDonationDetails({
      ...donationDetails,
      [name]: name === "valuationAmount" ? parseFloat(value) || "" : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveInProgress(true);
    setError(null);

    try {
      // 1. Mettre à jour l'œuvre : availability = 'donated'
      const { error: updateError } = await supabase
        .from("artworks")
        .update({ 
          availability: 'donated',
          updated_at: new Date().toISOString()
        })
        .eq("id", artwork.id);

      if (updateError) throw updateError;

      // 2. Optionnel : créer une entrée dans une table "donations" si elle existe
      // const { error: donationError } = await supabase
      //   .from("donations")
      //   .insert([{
      //     artwork_id: artwork.id,
      //     client_id: artwork.client_id,
      //     valuation_amount: donationDetails.valuationAmount,
      //     recipient: donationDetails.recipient,
      //     donation_date: donationDetails.date,
      //     notes: donationDetails.notes
      //   }]);
      // if (donationError) throw donationError;

      // 3. Callback de succès
      if (onSubmit) {
        onSubmit(donationDetails);
      }

      onClose();
    } catch (err) {
      console.error("Erreur lors de la donation:", err);
      setError(err.message || t("artworkModals.donation.errors.save"));
    } finally {
      setSaveInProgress(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
         onClick={onClose}>
      <div className="bg-white p-8 rounded-md max-w-lg w-full"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h3 className="font-serif text-xl">{t("artworkModals.donation.title")}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="valuationAmount" className="block text-sm font-medium text-gray-dark mb-1">
                {t("artworkModals.donation.fields.valuation")}
              </label>
              <input
                type="number"
                id="valuationAmount"
                name="valuationAmount"
                value={donationDetails.valuationAmount}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
                className="w-full border border-gray-medium p-2 rounded"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("artworkModals.donation.receiptHelp")}
              </p>
            </div>
            
            <div>
              <label htmlFor="recipient" className="block text-sm font-medium text-gray-dark mb-1">
                {t("artworkModals.donation.fields.recipient")}
              </label>
              <input
                type="text"
                id="recipient"
                name="recipient"
                value={donationDetails.recipient}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-medium p-2 rounded"
                placeholder={t("artworkModals.donation.placeholders.recipient")}
              />
            </div>
            
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-dark mb-1">
                {t("artworkModals.donation.fields.date")}
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={donationDetails.date}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-medium p-2 rounded"
              />
            </div>
            
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-dark mb-1">
                {t("artworkModals.common.notes")}
              </label>
              <textarea
                id="notes"
                name="notes"
                rows="3"
                value={donationDetails.notes}
                onChange={handleInputChange}
                className="w-full border border-gray-medium p-2 rounded"
                placeholder={t("artworkModals.donation.placeholders.notes")}
              ></textarea>
            </div>
          </div>
          
          <div className="mt-8 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn-artsy btn-secondary-artsy mr-2"
              disabled={saveInProgress}
            >
              {t("artworkModals.common.cancel")}
            </button>
            <button 
              type="submit" 
              disabled={saveInProgress}
              className="btn-artsy btn-primary-artsy"
            >
              {saveInProgress ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t("artworkModals.common.saving")}
                </span>
              ) : t("artworkModals.donation.actions.confirm")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DonationModal;